/**
 * Watches (Bevakningar) tab on the Favourites page.
 *
 * Handles:
 *  - Tab switching between Favoriter and Bevakningar
 *  - Fetching the user's watches from the backend API
 *  - Rendering watch groups with horizontal-scrolling product cards
 *  - Three-dot menu with "Go to watch" and "Delete watch" actions
 *  - "Bevakningar utan nytt" section for watches with no products
 */
(function () {
  'use strict';

  var CONCURRENT_REQUESTS = 6;
  var MAX_PREVIEW_CARDS = 12;
  var watchesLoaded = false;

  // ---------------------------------------------------------------------------
  // DOM helpers
  // ---------------------------------------------------------------------------

  function getSectionRoot() {
    return document.querySelector('[id^="favourites-section-"]');
  }

  function getSectionId() {
    var root = getSectionRoot();
    return root ? root.dataset.sectionId : null;
  }

  function getLocaleRoot() {
    var root = getSectionRoot();
    if (!root) return '';
    var localeRoot = root.getAttribute('data-locale-root') || '';
    if (localeRoot === '/') return '';
    return localeRoot;
  }

  function showEl(el, show) {
    if (!el) return;
    el.classList.toggle('hidden', !show);
    el.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  // ---------------------------------------------------------------------------
  // API helpers
  // ---------------------------------------------------------------------------

  function getApiBase() {
    return window.API_BASE_URL
      ? window.API_BASE_URL.replace(/\/shopify\/?$/, '')
      : '';
  }

  function refreshIdToken() {
    return new Promise(function (resolve, reject) {
      var auth = window.firebaseAuth;
      if (!auth || typeof auth.waitForAuthState !== 'function') {
        reject(new Error('Firebase auth not available'));
        return;
      }
      auth.waitForAuthState(3000).then(function (user) {
        if (!user) {
          reject(new Error('No Firebase user'));
          return;
        }
        user.getIdToken(true).then(function (newToken) {
          try {
            sessionStorage.setItem('firebaseIdToken', newToken);
          } catch (ignore) { void ignore; }
          resolve(newToken);
        }).catch(reject);
      }).catch(reject);
    });
  }

  function apiFetch(url, options) {
    options = options || {};

    function doFetch(token) {
      var headers = Object.assign(
        { 'Content-Type': 'application/json' },
        options.headers || {},
        token ? { 'Authorization': 'Bearer ' + token } : {}
      );
      return fetch(url, Object.assign({}, options, { headers: headers }));
    }

    var cachedToken = null;
    try {
      cachedToken = sessionStorage.getItem('firebaseIdToken');
    } catch (ignore) { void ignore; }

    // If no token has been cached yet, wait for Firebase auth to mint one
    // before firing — otherwise the request goes out unauthenticated and
    // the backend returns 401 in a race with auth init.
    var firstAttempt = cachedToken
      ? doFetch(cachedToken)
      : refreshIdToken().then(doFetch).catch(function () { return doFetch(null); });

    return firstAttempt.then(function (response) {
      if (response.status !== 401) return response;
      // Retry once with a freshly minted token on any 401, regardless of
      // the error wording in the body.
      return refreshIdToken().then(doFetch).catch(function () { return response; });
    });
  }

  // ---------------------------------------------------------------------------
  // Section Rendering API
  // ---------------------------------------------------------------------------

  function isValidCardHtml(html) {
    if (!html || typeof html !== 'string') return false;
    var trimmed = html.trim();
    if (trimmed.length < 100) return false;
    var hasImg = trimmed.indexOf('<img') !== -1;
    var hasSource = trimmed.indexOf('src=') !== -1 || trimmed.indexOf('srcset=') !== -1;
    return hasImg && hasSource;
  }

  function renderCardHtml(handle) {
    var localeRoot = getLocaleRoot();
    var prefix = localeRoot || '';
    var url = prefix + '/products/' + encodeURIComponent(handle) + '?sections=watches-card';
    return fetch(url)
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (!data || !data['watches-card']) return null;
        var html = data['watches-card'];
        // Extract published_date from the data attribute on the wrapper div
        var publishedDate = null;
        var match = html.match(/data-published-date="([^"]*)"/);
        if (match && match[1]) {
          publishedDate = match[1];
        }
        return { html: html, publishedDate: publishedDate };
      })
      .catch(function () { return null; });
  }

  /**
   * Fetch card HTML for all handles and return array of { html, publishedDate }.
   */
  function fetchCardsWithDates(handles) {
    var startIndex = 0;
    var allCards = [];

    function processNextBatch() {
      var batch = handles.slice(startIndex, startIndex + CONCURRENT_REQUESTS);
      if (!batch.length) return Promise.resolve(allCards);
      var batchStart = startIndex;
      startIndex += batch.length;

      var promises = batch.map(function (handle, i) {
        return renderCardHtml(handle).then(function (result) {
          return { index: batchStart + i, html: result ? result.html : null, publishedDate: result ? result.publishedDate : null };
        });
      });

      return Promise.all(promises).then(function (results) {
        results.sort(function (a, b) { return a.index - b.index; });
        results.forEach(function (r) { allCards.push(r); });
        return processNextBatch();
      });
    }

    return processNextBatch();
  }

  function extractImageUrl(html) {
    if (!html) return null;
    // Match the first img src attribute in the card HTML
    var match = html.match(/<img[^>]+src="([^"]+)"/);
    return match ? match[1] : null;
  }

  function parseDate(value) {
    if (!value) return null;
    // Handle Firestore Timestamp objects with _seconds
    if (typeof value === 'object' && value._seconds) {
      return new Date(value._seconds * 1000);
    }
    var d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  // ---------------------------------------------------------------------------
  // Tab switching
  // ---------------------------------------------------------------------------

  function initTabs() {
    var sectionId = getSectionId();
    if (!sectionId) return;

    var tabsWrapper = document.getElementById('favourites-tabs-wrapper-' + sectionId);
    var tabFavoriter = document.getElementById('tab-favoriter-' + sectionId);
    var tabBevakningar = document.getElementById('tab-bevakningar-' + sectionId);
    var contentEl = document.getElementById('favourites-content-' + sectionId);
    var emptyEl = document.getElementById('favourites-empty-' + sectionId);
    var panelWatches = document.getElementById('watches-content-' + sectionId);

    if (!tabFavoriter || !tabBevakningar) return;

    // Track which favourites element was visible before switching to bevakningar
    var favouritesVisibleEl = null;
    var hasWatches = false;

    // Check if user has watches before showing tabs
    function checkForWatches() {
      var base = getApiBase();
      if (!base) return Promise.resolve(false);
      return apiFetch(base + '/shopify/watches', { method: 'GET' })
        .then(function (res) {
          return res.ok ? res.json() : { watches: [] };
        })
        .then(function (data) {
          var watches = (data && data.watches) || [];
          return watches.length > 0;
        })
        .catch(function () { return false; });
    }

    // Show tabs once favourites.js finishes loading AND we know watches exist
    function waitForFavouritesLoad() {
      var favouritesReady = false;
      var watchCheckDone = false;

      checkForWatches().then(function (result) {
        hasWatches = result;
        watchCheckDone = true;
        maybeShowTabs();
      });

      var observer = new MutationObserver(function () {
        var contentVisible = contentEl && !contentEl.classList.contains('hidden');
        var emptyVisible = emptyEl && !emptyEl.classList.contains('hidden');
        if (contentVisible || emptyVisible) {
          observer.disconnect();
          favouritesVisibleEl = contentVisible ? contentEl : emptyEl;
          favouritesReady = true;
          maybeShowTabs();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

      function maybeShowTabs() {
        if (!favouritesReady || !watchCheckDone) return;
        if (hasWatches) {
          showEl(tabsWrapper, true);
        }
      }
    }
    waitForFavouritesLoad();

    function hasTabParam() {
      var params = new URLSearchParams(window.location.search);
      return params.get('tab') === 'watches';
    }

    function switchTab(tab, updateUrl) {
      if (tab === 'favoriter') {
        tabFavoriter.className = 'favourites-tab favourites-tab--active';
        tabBevakningar.className = 'favourites-tab favourites-tab--inactive';
        // Restore whichever favourites element was visible before
        if (favouritesVisibleEl) {
          showEl(favouritesVisibleEl, true);
        }
        if (panelWatches) showEl(panelWatches, false);
        if (updateUrl && hasTabParam()) {
          var params = new URLSearchParams(window.location.search);
          params.delete('tab');
          var qs = params.toString();
          var newUrl = window.location.pathname + (qs ? '?' + qs : '');
          history.replaceState(null, '', newUrl);
        }
      } else {
        tabBevakningar.className = 'favourites-tab favourites-tab--active';
        tabFavoriter.className = 'favourites-tab favourites-tab--inactive';
        // Remember which element is currently visible, then hide both
        if (contentEl && !contentEl.classList.contains('hidden')) {
          favouritesVisibleEl = contentEl;
        } else if (emptyEl && !emptyEl.classList.contains('hidden')) {
          favouritesVisibleEl = emptyEl;
        }
        showEl(contentEl, false);
        showEl(emptyEl, false);
        if (panelWatches) showEl(panelWatches, true);
        if (!watchesLoaded) {
          watchesLoaded = true;
          loadWatches();
        }
        if (updateUrl && !hasTabParam()) {
          var params = new URLSearchParams(window.location.search);
          params.set('tab', 'watches');
          history.replaceState(null, '', window.location.pathname + '?' + params.toString());
        }
      }
    }

    tabFavoriter.addEventListener('click', function () { switchTab('favoriter', true); });
    tabBevakningar.addEventListener('click', function () { switchTab('bevakningar', true); });

    // Check URL param for direct navigation (e.g. back button from a watch page)
    if (hasTabParam()) {
      var paramInterval = setInterval(function () {
        if (!hasWatches) return;
        var contentVisible = contentEl && !contentEl.classList.contains('hidden');
        var emptyVisible = emptyEl && !emptyEl.classList.contains('hidden');
        var tabsVisible = tabsWrapper && !tabsWrapper.classList.contains('hidden');
        if (tabsVisible && (contentVisible || emptyVisible)) {
          clearInterval(paramInterval);
          favouritesVisibleEl = contentVisible ? contentEl : emptyEl;
          switchTab('bevakningar', false);
        }
      }, 100);
      // Safety: stop checking after 10s
      setTimeout(function () { clearInterval(paramInterval); }, 10000);
    }
  }

  // ---------------------------------------------------------------------------
  // Fetch and render watches
  // ---------------------------------------------------------------------------

  function loadWatches() {
    var sectionId = getSectionId();
    if (!sectionId) return;

    var base = getApiBase();
    if (!base) return;

    var spinnerEl = document.getElementById('watches-spinner-' + sectionId);
    var listEl = document.getElementById('watches-list-' + sectionId);
    var emptyEl = document.getElementById('watches-empty-' + sectionId);

    showEl(spinnerEl, true);
    showEl(emptyEl, false);

    apiFetch(base + '/shopify/watches', { method: 'GET' })
      .then(function (res) {
        return res.ok ? res.json() : { watches: [] };
      })
      .then(function (data) {
        var watches = (data && data.watches) || [];

        if (watches.length === 0) {
          showEl(spinnerEl, false);
          showEl(emptyEl, true);
          return;
        }

        // For each watch, fetch product cards and determine which are "new".
        // `lastProductIds` holds Algolia objectIDs (used for diffing on the
        // backend); `lastProductHandles` holds Shopify handles, which is what
        // the Section Rendering API needs.
        var watchPromises = watches.map(function (watch) {
          var source = (watch.lastProductHandles && watch.lastProductHandles.length)
            ? watch.lastProductHandles
            : (watch.lastProductIds || []);
          var handles = source.slice(0, MAX_PREVIEW_CARDS);
          if (!handles.length) {
            return Promise.resolve({ watch: watch, newCards: [], hasNew: false });
          }
          return fetchCardsWithDates(handles).then(function (cards) {
            var createdAt = parseDate(watch.createdAt);
            var newCards = [];
            var firstImage = null;
            cards.forEach(function (card) {
              if (card.html && isValidCardHtml(card.html)) {
                // Extract image URL from the first valid card for the thumbnail
                if (!firstImage) {
                  firstImage = extractImageUrl(card.html);
                }
                var publishedDate = parseDate(card.publishedDate);
                if (createdAt && publishedDate && publishedDate > createdAt) {
                  newCards.push(card);
                }
              }
            });
            return { watch: watch, newCards: newCards, hasNew: newCards.length > 0, firstImage: firstImage };
          });
        });

        Promise.all(watchPromises).then(function (results) {
          showEl(spinnerEl, false);

          var withNew = results.filter(function (r) { return r.hasNew; });
          var withoutNew = results.filter(function (r) { return !r.hasNew; });

          // Render watches with new items (large cards)
          withNew.forEach(function (r) {
            var group = createWatchGroup(r.watch);
            listEl.appendChild(group);
            var scrollEl = group.querySelector('.watch-group__scroll');
            r.newCards.forEach(function (card) {
              var div = document.createElement('div');
              div.className = 'watch-card-item';
              div.innerHTML = card.html;
              scrollEl.appendChild(div);
            });
          });

          // Render watches without new items
          if (withoutNew.length > 0) {
            var noNewItems = withoutNew.map(function (r) {
              return { watch: r.watch, firstImage: r.firstImage };
            });
            var noNewSection = createNoNewSection(noNewItems);
            listEl.appendChild(noNewSection);
          }

          // If all watches ended up empty (no products at all)
          if (withNew.length === 0 && withoutNew.length === 0) {
            showEl(emptyEl, true);
          }
        });
      })
      .catch(function (err) {
        console.error('[watches] error loading watches', err);
        showEl(spinnerEl, false);
        showEl(emptyEl, true);
      });
  }

  // ---------------------------------------------------------------------------
  // DOM construction
  // ---------------------------------------------------------------------------

  function watchUrlWithId(url, fsId) {
    if (!url || !fsId) return url || '';
    try {
      var parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('watchId', fsId);
      return parsed.toString();
    } catch (e) {
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'watchId=' + encodeURIComponent(fsId);
    }
  }

  function cleanSubtitle(subtitle) {
    if (!subtitle) return '';
    // Remove known junk phrases inline before splitting
    var cleaned = subtitle
      .replace(/\s*Ta bort filter\s*/gi, '')
      .replace(/\s*Remove all\s*/gi, '');
    return cleaned
      .split(/,\s*/)
      .filter(function (part) {
        return part.indexOf('gid://') === -1;
      })
      .map(function (part) {
        // Strip label prefixes like "Storlek: M" → "M"
        var colonIdx = part.indexOf(': ');
        return colonIdx !== -1 ? part.substring(colonIdx + 2).trim() : part.trim();
      })
      .filter(function (part) { return part.length > 0; })
      .join(', ');
  }

  // Types where the value alone is ambiguous and needs a prefix (e.g. "44" → "Storlek: 44")
  var TYPES_WITH_PREFIX = { size: true, price: true };

  function formatFilter(f) {
    // f = { label, value, type } from backend, OR plain string from legacy fallback
    if (typeof f === 'string') return f;
    if (!f || !f.value) return '';
    if (f.type && TYPES_WITH_PREFIX[f.type] && f.label) {
      return f.label + ': ' + f.value;
    }
    return f.value;
  }

  function getWatchFilters(watch) {
    // Prefer structured filters from backend; fall back to parsing subtitle.
    if (watch.filters && watch.filters.length) {
      return watch.filters;
    }
    var cleaned = cleanSubtitle(watch.subtitle);
    if (!cleaned) return [];
    return cleaned.split(/,\s*/).filter(function (p) { return p.length > 0; });
  }

  function showFilterBottomsheet(filters) {
    var locale = window.watchesLocale || {};
    var contentEl = document.getElementById('watch-filters-content');
    if (!contentEl) return;

    var html = '<h2 class="watch-filters-bottomsheet__title">' +
      escapeHtml(locale.active_filters_title || 'Active filters') + '</h2>' +
      '<div class="watch-filters-bottomsheet__list">';
    filters.forEach(function (f) {
      var display = formatFilter(f);
      if (display) {
        html += '<span class="watch-filters-bottomsheet__pill">' + escapeHtml(display) + '</span>';
      }
    });
    html += '</div>';
    contentEl.innerHTML = html;

    if (window.BottomsheetManager) {
      window.BottomsheetManager.openBottomsheet('watch-filters');
    }
  }

  function showDeleteConfirmBottomsheet(watchTitle, onConfirm) {
    var locale = window.watchesLocale || {};
    var textEl = document.getElementById('watch-delete-confirm-text');
    var buttonEl = document.getElementById('watch-delete-confirm-button');
    if (!textEl || !buttonEl) return;

    var template = locale.delete_confirm_text || 'Are you sure you want to delete the watch for {{ title }}?';
    textEl.textContent = template.replace('{{ title }}', watchTitle || '').replace('__TITLE__', watchTitle || '');
    buttonEl.textContent = locale.delete_confirm_button || 'Yes, delete watch';

    // Reset listener (clone to wipe previous handlers)
    var newButton = buttonEl.cloneNode(true);
    buttonEl.parentNode.replaceChild(newButton, buttonEl);
    newButton.addEventListener('click', function () {
      if (window.BottomsheetManager) {
        window.BottomsheetManager.closeBottomsheet('watch-delete-confirm');
      }
      onConfirm();
    });

    if (window.BottomsheetManager) {
      window.BottomsheetManager.openBottomsheet('watch-delete-confirm');
    }
  }

  function buildFilterCountHtml(watch) {
    var locale = window.watchesLocale || {};
    var filters = getWatchFilters(watch);
    if (!filters.length) return '';
    return '<button type="button" class="watch-group__filter-count" data-filters="' +
      escapeAttr(JSON.stringify(filters)) + '">' +
      filters.length + ' ' + escapeHtml(locale.active_filters || 'active filters') +
      '</button>';
  }

  function createWatchGroup(watch) {
    var locale = window.watchesLocale || {};
    var group = document.createElement('div');
    group.className = 'watch-group';
    group.setAttribute('data-watch-id', watch.fsId);

    var title = watch.title || '';
    var watchUrl = watchUrlWithId(watch.url, watch.fsId);
    var filterCountHtml = buildFilterCountHtml(watch);

    group.innerHTML =
      '<div class="watch-group__header">' +
        '<div class="watch-group__title-row">' +
          '<h2 class="watch-group__title"><a href="' + escapeAttr(watchUrl) + '" class="watch-group__title-link">' + escapeHtml(title) + '</a></h2>' +
          filterCountHtml +
        '</div>' +
        '<div class="watch-group__menu-wrapper">' +
          '<button type="button" class="watch-group__menu-btn" aria-label="Menu"><span class="menu-dot"></span><span class="menu-dot"></span><span class="menu-dot"></span></button>' +
          '<div class="watch-group__dropdown">' +
            '<button type="button" class="watch-group__dropdown-item" data-action="navigate">' +
              escapeHtml(locale.go_to_watch || 'Go to watch') +
            '</button>' +
            '<button type="button" class="watch-group__dropdown-item" data-action="delete">' +
              escapeHtml(locale.delete_watch || 'Delete watch') +
            '</button>' +
          '</div>' +
        '</div>' +
      '</div>' +
      '<div class="watch-group__scroll"></div>';

    // Menu toggle
    var menuBtn = group.querySelector('.watch-group__menu-btn');
    var dropdown = group.querySelector('.watch-group__dropdown');

    menuBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      // Close any other open menus
      document.querySelectorAll('.watch-group__dropdown.open').forEach(function (d) {
        if (d !== dropdown) d.classList.remove('open');
      });
      dropdown.classList.toggle('open');
    });

    // Navigate action
    group.querySelector('[data-action="navigate"]').addEventListener('click', function () {
      dropdown.classList.remove('open');
      var url = watchUrlWithId(watch.url, watch.fsId);
      if (url) {
        window.location.href = url;
      }
    });

    // Delete action (with confirmation)
    group.querySelector('[data-action="delete"]').addEventListener('click', function () {
      dropdown.classList.remove('open');
      showDeleteConfirmBottomsheet(watch.title || '', function () {
        deleteWatch(watch.fsId, group);
      });
    });

    // Filter count click
    var filterBtn = group.querySelector('.watch-group__filter-count');
    if (filterBtn) {
      filterBtn.addEventListener('click', function () {
        var filters = JSON.parse(filterBtn.getAttribute('data-filters'));
        showFilterBottomsheet(filters);
      });
    }

    return group;
  }

  function createNoNewSection(items) {
    var locale = window.watchesLocale || {};
    var section = document.createElement('div');
    section.className = 'watches-no-new';

    var html = '<h3 class="watches-no-new__title">' +
      escapeHtml(locale.no_new_section_title || 'Watches with no new items') + '</h3>';

    items.forEach(function (item) {
      var watch = item.watch;
      var title = watch.title || '';
      var filterCountHtml = buildFilterCountHtml(watch);

      var thumbHtml = item.firstImage
        ? '<img class="watches-no-new__thumb" src="' + escapeAttr(item.firstImage) + '" alt="" loading="lazy">'
        : '<div class="watches-no-new__thumb"></div>';

      html +=
        '<div class="watches-no-new__item" data-watch-id="' + escapeAttr(watch.fsId) + '">' +
          '<a href="' + escapeAttr(watchUrlWithId(watch.url, watch.fsId)) + '" class="watches-no-new__item-left">' +
            thumbHtml +
            '<div class="watches-no-new__item-info">' +
              '<span class="watches-no-new__item-title">' + escapeHtml(title) + '</span>' +
              '<span class="watches-no-new__item-count">0 ' + escapeHtml(locale.new_items || 'new') + '</span>' +
            '</div>' +
          '</a>' +
          filterCountHtml +
          '<div class="watch-group__menu-wrapper">' +
            '<button type="button" class="watch-group__menu-btn" aria-label="Menu"><span class="menu-dot"></span><span class="menu-dot"></span><span class="menu-dot"></span></button>' +
            '<div class="watch-group__dropdown">' +
              '<button type="button" class="watch-group__dropdown-item" data-action="navigate" data-url="' + escapeAttr(watchUrlWithId(watch.url, watch.fsId)) + '">' +
                escapeHtml(locale.go_to_watch || 'Go to watch') +
              '</button>' +
              '<button type="button" class="watch-group__dropdown-item" data-action="delete" data-fsid="' + escapeAttr(watch.fsId) + '" data-title="' + escapeAttr(watch.title || '') + '">' +
                escapeHtml(locale.delete_watch || 'Delete watch') +
              '</button>' +
            '</div>' +
          '</div>' +
        '</div>';
    });

    section.innerHTML = html;

    // Attach event listeners for each item
    section.querySelectorAll('.watches-no-new__item').forEach(function (item) {
      var menuBtn = item.querySelector('.watch-group__menu-btn');
      var dropdown = item.querySelector('.watch-group__dropdown');

      menuBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        document.querySelectorAll('.watch-group__dropdown.open').forEach(function (d) {
          if (d !== dropdown) d.classList.remove('open');
        });
        dropdown.classList.toggle('open');
      });

      item.querySelector('[data-action="navigate"]').addEventListener('click', function () {
        dropdown.classList.remove('open');
        var url = this.getAttribute('data-url');
        if (url) window.location.href = url;
      });

      item.querySelector('[data-action="delete"]').addEventListener('click', function () {
        dropdown.classList.remove('open');
        var fsId = this.getAttribute('data-fsid');
        var title = this.getAttribute('data-title') || '';
        showDeleteConfirmBottomsheet(title, function () {
          deleteWatch(fsId, item);
        });
      });

      var filterBtn = item.querySelector('.watch-group__filter-count');
      if (filterBtn) {
        filterBtn.addEventListener('click', function (e) {
          e.preventDefault();
          var filters = JSON.parse(filterBtn.getAttribute('data-filters'));
          showFilterBottomsheet(filters);
        });
      }
    });

    return section;
  }


  // ---------------------------------------------------------------------------
  // Delete watch
  // ---------------------------------------------------------------------------

  function deleteWatch(fsId, domEl) {
    if (!fsId) return;

    var base = getApiBase();
    apiFetch(base + '/shopify/watch/' + encodeURIComponent(fsId), { method: 'DELETE' })
      .then(function () {
        // Remove from DOM
        if (domEl) domEl.remove();

        // Update watches cookie for consistency with collection page watch buttons
        try {
          var cookieValue = getCookie('watches');
          if (cookieValue) {
            var watches = JSON.parse(cookieValue);
            var updated = watches.filter(function (w) { return w.fsId !== fsId; });
            setCookieValue('watches', JSON.stringify(updated), 365);
          }
        } catch (ignore) { void ignore; }

        // Show toast
        var locale = window.watchesLocale || {};
        if (typeof window.showConfirmationToast === 'function') {
          window.showConfirmationToast(locale.deleted || 'Watch removed');
        }

        // Check if all watches are now removed
        var sectionId = getSectionId();
        var listEl = document.getElementById('watches-list-' + sectionId);
        var emptyEl = document.getElementById('watches-empty-' + sectionId);
        if (listEl && listEl.children.length === 0) {
          showEl(emptyEl, true);
        }
      })
      .catch(function (err) {
        console.error('[watches] error deleting watch', err);
      });
  }

  // ---------------------------------------------------------------------------
  // Cookie helpers (mirror global.js patterns)
  // ---------------------------------------------------------------------------

  function getCookie(name) {
    var value = '; ' + document.cookie;
    var parts = value.split('; ' + name + '=');
    if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
    return null;
  }

  function setCookieValue(name, value, days) {
    var expires = '';
    if (days) {
      var d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + d.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/';
  }

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function escapeAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ---------------------------------------------------------------------------
  // Close dropdown on outside click
  // ---------------------------------------------------------------------------

  document.addEventListener('click', function () {
    document.querySelectorAll('.watch-group__dropdown.open').forEach(function (d) {
      d.classList.remove('open');
    });
  });

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTabs);
  } else {
    initTabs();
  }
})();
